// src/libs/services/arSpace.ts

import { publicClient, apiClient } from './axiosInstance';
import { pickDefined, ensureOk, toFormData } from '../helpers/api-helper';
import type { ApiEnvelope } from '@/types/apiEnvelope';
import type {
  ArSpace,
  ArSpaceGroup,
  ArSpaceListParams,
  ArSpaceListData,
  ArSpaceIndexData,
  ArSpaceIndexItem,
  ArSpaceDetail,
  ArSpaceUserDetail,
  ArSpaceStoreDetail,
  ArSpaceListResponse,
  ArSpaceCreateFormPayload,
  ArSpaceCreateFormResult,
  ArSpaceUpdateFormPayload,
  ArSpaceUpdateFormResult,
} from '@/types/arSpace';

// --------------------------------------------------------
// Helper 原始型別：後端 /ar_space/branch/{id} 的回傳結構
// （用來避免 any，欄位抓寬一點）
// --------------------------------------------------------
type ArSpaceStoreDetailRaw = {
  branch_id?: string | number | null;
  id?: string | number | null;
  store_id?: string | number | null;

  name?: string | null;
  photo?: string | null;
  title?: string | null;

  ar_image?: string | null;
  ar_background?: string | null;

  city?: string | null;
  district?: string | null;
  address?: string | null;
  url?: string | null;
  latitude?: string | null;
  longitude?: string | null;

  article_num?: string | number | null;
  look_num?: string | number | null;
  share_num?: string | number | null;
  message_num?: string | number | null;

  crtime?: string | null;
  letime?: string | null;

  article_data?: unknown;

  store_name?: string | null;
  store_photo?: string | null;
  store_open_id?: string | null;
  logo_photo?: string | null;

  // 後端可能會塞其他欄位，保險起見開一個索引簽章
  [key: string]: unknown;
};

// --------------------------------------------------------
// Helper：後端 /ar_space/branch/{id} → 正規化成 ArSpaceStoreDetail
// --------------------------------------------------------
function normalizeArSpaceStoreDetail(
  raw: ArSpaceStoreDetailRaw | null | undefined
): ArSpaceStoreDetail {
  if (!raw) {
    throw new Error('[ArSpace.store.detail] 回傳空物件');
  }

  // 以 branch_id 為主，沒有就退而求其次用 id
  const branchId = String(raw.branch_id ?? raw.id ?? '');

  const detail: ArSpaceStoreDetail = {
    ar_type: 'branch',
    id: branchId,
    branch_id: branchId,
    store_id: Number(raw.store_id ?? 0),

    name: raw.name ?? '',
    photo: raw.photo ?? '',
    title: raw.title ?? '',

    ar_image: raw.ar_image ?? '',
    ar_background: raw.ar_background ?? '',

    city: raw.city ?? '',
    district: raw.district ?? '',
    address: raw.address ?? '',
    url: raw.url ?? '',
    latitude: raw.latitude ?? '',
    longitude: raw.longitude ?? '',

    article_num: Number(raw.article_num ?? 0),
    look_num: Number(raw.look_num ?? 0),
    share_num: Number(raw.share_num ?? 0),
    message_num: Number(raw.message_num ?? 0),

    crtime: raw.crtime ?? '',
    letime: raw.letime ?? '',

    article_data: Array.isArray(raw.article_data) ? raw.article_data : [],

    store_name: raw.store_name ?? undefined,
    store_photo: raw.store_photo ?? undefined,
    store_open_id: raw.store_open_id ?? undefined,
    logo_photo: raw.logo_photo ?? undefined,
  };

  return detail;
}

export const ArSpaceService = {
  /**
   * 取得 AR 空間清單（需要登入）
   * - 預設使用 apiClient（自動附帶 Authorization）
   * - 若後端開放匿名取得，傳 { withAuth:false } 會改用 publicClient
   */
  async list(params: ArSpaceListParams, opts?: { withAuth?: boolean }) {
    const client = opts?.withAuth === false ? publicClient : apiClient;

    const res = await client.get<ApiEnvelope<ArSpaceListData>>('/ar_space/mix', {
      params: pickDefined(params),
    });

    // 有些後端此處 data 直接是陣列；這裡兼容轉換成標準物件
    const raw = res.data;

    // 印出後端原始包裝的 data(debug用)
    console.log('[ArSpace.list] raw response:', res.data);

    let data = raw.data;

    // 如果上游直接給 array，轉成 ArSpaceListData
    if (Array.isArray(data)) {
      data = {
        items: data as ArSpace[],
        page: Number(params.page ?? 1),
        page_size: (data as ArSpace[]).length,
        total: (data as ArSpace[]).length,
      } satisfies ArSpaceListData;
      return data;
    }

    return ensureOk<ArSpaceListData>(raw);
  },

  /**
   * 取得 AR 主題群組（GET /ar_space/group）
   * 回傳：ArSpaceGroup[]
   */
  async getArSpaceGroup(opts?: {
    num?: number;
    start?: number;
    withAuth?: boolean;
  }): Promise<ArSpaceGroup[]> {
    const num = opts?.num ?? 50;
    const start = opts?.start ?? 0;

    const client = opts?.withAuth === false ? publicClient : apiClient;

    const { data } = await client.get<ApiEnvelope<ArSpaceGroup[]>>('/ar_space/group', {
      params: { num, start },
    });

    return ensureOk<ArSpaceGroup[]>(data);
  },

  /**
   * 取得自己 AR 空間列表（GET /ar_space/list）
   */
  async getMyArSpace() {
    const { data } = await apiClient.get<ApiEnvelope<ArSpaceListResponse>>('/ar_space/list');

    return ensureOk<ArSpaceListResponse>(data);
  },

  /**
   * 取得單一 AR 空間（GET /ar_space/{id}）
   */
  async getUnitArSpace(id: number | string, opts?: { withAuth?: boolean }) {
    const client = opts?.withAuth === false ? publicClient : apiClient;
    const { data } = await client.get<ApiEnvelope<ArSpaceDetail>>(`/ar_space/${id}`);

    // 印出後端原始包裝的 data(debug用)
    //console.log('[ArSpace.detail] raw response:', data);

    return ensureOk<ArSpaceDetail>(data);
  },

  /**
   * 取得首頁ar_space 空間資料（GET /ar_space/index_ar_space）
   * ✅ 後端已改成「不回 article_data」的輕量版
   */
  async getIndexArSpace(opts?: { withAuth?: boolean }): Promise<ArSpaceIndexItem[]> {
    const client = opts?.withAuth === false ? publicClient : apiClient;

    // ✅ lite 回傳：沒有 article_data
    type ArSpaceIndexDataLite = Omit<ArSpaceIndexData, 'article_data'>;

    const { data } = await client.get<ApiEnvelope<ArSpaceIndexDataLite[]>>(
      '/ar_space/index_ar_space'
    );

    const rawList = ensureOk<ArSpaceIndexDataLite[]>(data);

    // ✅ 為了不改動你全站型別（ArSpaceIndexItem 可能還期待 article_data）
    //    這裡在前端補一個空陣列，讓舊 code 不會因為缺欄位 TS error
    const normalizedList: ArSpaceIndexItem[] = rawList.map((item) => ({
      ...(item as unknown as ArSpaceIndexData),
      id: `${item.type}_${item.type_id}`,
      article_data: [],
    }));

    return normalizedList;
  },

  /**
   * ✅ 首頁用：依 type + type_id 拿該空間的 article_data
   * - ar_space → /ar_space/ar_space/{id}
   * - branch   → /ar_space/branch/{id}
   */
  async getIndexSpaceArticles(
    params: { type: 'ar_space' | 'branch'; type_id: number | string },
    opts?: { withAuth?: boolean }
  ): Promise<NonNullable<ArSpaceIndexItem['article_data']>> {
    if (params.type === 'branch') {
      const detail = await ArSpaceService.getStoreArSpace(params.type_id, opts);
      return detail.article_data ?? [];
    }

    const detail = await ArSpaceService.getUserArSpace(params.type_id, opts);
    return detail.article_data ?? [];
  },

  /**
   * 取得個人自建ar_space 空間資料（GET /ar_space/ar_space/{id}）
   *
   */
  async getUserArSpace(id: number | string, opts?: { withAuth?: boolean }) {
    const client = opts?.withAuth === false ? publicClient : apiClient;

    const { data } = await client.get<ApiEnvelope<ArSpaceUserDetail>>(`/ar_space/ar_space/${id}`);

    // 印出後端原始包裝的 data(debug用)
    //console.log('[ArSpace.user.detail] raw response:', data);

    return ensureOk<ArSpaceUserDetail>(data);
  },

  /**
   * 取得分店ar空間資料（GET /ar_space/branch/{id}）
   *
   * 後端目前回來的不是完全符合 ArSpaceStoreDetail 的結構，
   * 所以先用 ArSpaceStoreDetailRaw 接住，然後再 normalize。
   */
  async getStoreArSpace(id: number | string, opts?: { withAuth?: boolean }) {
    const client = opts?.withAuth === false ? publicClient : apiClient;

    // 這裡的 data 是 ApiEnvelope<ArSpaceStoreDetailRaw>
    const { data } = await client.get<ApiEnvelope<ArSpaceStoreDetailRaw>>(`/ar_space/branch/${id}`);

    //console.log('[ArSpace.store.detail] raw envelope:', data);

    // 取出真正的 data（資料物件）
    const raw = ensureOk<ArSpaceStoreDetailRaw>(data);

    //console.log('[ArSpace.store.detail] raw data:', raw);

    // 正規化：補上 ar_type / id / branch_id 等
    const normalized = normalizeArSpaceStoreDetail(raw);

    //console.log('[ArSpace.store.detail] normalized:', normalized);

    return normalized;
  },

  /**
   * 建立個人 AR 空間（POST /ar_space）
   * - 必填：title, branch_name, ar_photo
   * - 選填：place_id, store_id
   * - Content-Type: multipart/form-data
   */
  async addNewArSpace(payload: ArSpaceCreateFormPayload): Promise<ArSpaceCreateFormResult> {
    const form = toFormData({
      title: payload.title,
      branch_name: payload.branch_name,
      ar_photo: payload.ar_photo,
      place_id: payload.place_id,
      store_id: payload.store_id ?? 0,
    });

    const { data } = await apiClient.post<ApiEnvelope<ArSpaceCreateFormResult>>('/ar_space', form);

    return ensureOk<ArSpaceCreateFormResult>(data);
  },

  /**
   * 修改個人 AR 空間（POST /ar_space/update）
   * - 必填：id, title
   * - 選填：ar_photo
   * - Content-Type: multipart/form-data
   */
  async updateArSpace(payload: ArSpaceUpdateFormPayload): Promise<ArSpaceUpdateFormResult> {
    const form = toFormData({
      id: payload.id,
      title: payload.title,
      ar_photo: payload.ar_photo,
    });

    const { data } = await apiClient.post<ApiEnvelope<ArSpaceUpdateFormResult>>(
      `/ar_space/update/${payload.id}`,
      form
    );

    return ensureOk<ArSpaceUpdateFormResult>(data);
  },

  async deleteUnitArSpace(id: number | string) {
    await apiClient.delete<ApiEnvelope>(`/ar_space/${id}`);
    return;
  },
};

// // src/libs/services/arSpace.ts

// import { publicClient, apiClient } from './axiosInstance';
// import { pickDefined, ensureOk, toFormData } from '../helpers/api-helper';
// import type { ApiEnvelope } from '@/types/apiEnvelope';
// import type {
//   ArSpace,
//   ArSpaceGroup,
//   ArSpaceListParams,
//   ArSpaceListData,
//   ArSpaceIndexData,
//   ArSpaceIndexItem,
//   ArSpaceDetail,
//   ArSpaceUserDetail,
//   ArSpaceStoreDetail,
//   ArSpaceListResponse,
//   ArSpaceCreateFormPayload,
//   ArSpaceCreateFormResult,
//   ArSpaceUpdateFormPayload,
//   ArSpaceUpdateFormResult,
//   OrderBy,
// } from '@/types/arSpace';

// // --------------------------------------------------------
// // Helper 原始型別：後端 /ar_space/branch/{id} 的回傳結構
// // （用來避免 any，欄位抓寬一點）
// // --------------------------------------------------------
// type ArSpaceStoreDetailRaw = {
//   branch_id?: string | number | null;
//   id?: string | number | null;
//   store_id?: string | number | null;

//   name?: string | null;
//   photo?: string | null;
//   title?: string | null;

//   ar_image?: string | null;
//   ar_background?: string | null;

//   city?: string | null;
//   district?: string | null;
//   address?: string | null;
//   url?: string | null;
//   latitude?: string | null;
//   longitude?: string | null;

//   article_num?: string | number | null;
//   look_num?: string | number | null;
//   share_num?: string | number | null;
//   message_num?: string | number | null;

//   crtime?: string | null;
//   letime?: string | null;

//   article_data?: unknown;

//   store_name?: string | null;
//   store_photo?: string | null;
//   store_open_id?: string | null;
//   logo_photo?: string | null;

//   [key: string]: unknown;
// };

// // --------------------------------------------------------
// // Helper：後端 /ar_space/branch/{id} → 正規化成 ArSpaceStoreDetail
// // --------------------------------------------------------
// function normalizeArSpaceStoreDetail(
//   raw: ArSpaceStoreDetailRaw | null | undefined
// ): ArSpaceStoreDetail {
//   if (!raw) {
//     throw new Error('[ArSpace.store.detail] 回傳空物件');
//   }

//   const branchId = String(raw.branch_id ?? raw.id ?? '');

//   const detail: ArSpaceStoreDetail = {
//     ar_type: 'branch',
//     id: branchId,
//     branch_id: branchId,
//     store_id: Number(raw.store_id ?? 0),

//     name: raw.name ?? '',
//     photo: raw.photo ?? '',
//     title: raw.title ?? '',

//     ar_image: raw.ar_image ?? '',
//     ar_background: raw.ar_background ?? '',

//     city: raw.city ?? '',
//     district: raw.district ?? '',
//     address: raw.address ?? '',
//     url: raw.url ?? '',
//     latitude: raw.latitude ?? '',
//     longitude: raw.longitude ?? '',

//     article_num: Number(raw.article_num ?? 0),
//     look_num: Number(raw.look_num ?? 0),
//     share_num: Number(raw.share_num ?? 0),
//     message_num: Number(raw.message_num ?? 0),

//     crtime: raw.crtime ?? '',
//     letime: raw.letime ?? '',

//     article_data: Array.isArray(raw.article_data) ? raw.article_data : [],

//     store_name: raw.store_name ?? undefined,
//     store_photo: raw.store_photo ?? undefined,
//     store_open_id: raw.store_open_id ?? undefined,
//     logo_photo: raw.logo_photo ?? undefined,
//   };

//   return detail;
// }

// // --------------------------------------------------------
// // ✅ 讓 order_by 穩定：預設 letime，且只允許已知值
// // --------------------------------------------------------
// function normalizeOrderBy(input: unknown): OrderBy {
//   const v = String(input ?? '').trim();
//   if (v === 'letime' || v === 'crtime' || v === 'distance' || v === 'article_num') return v;
//   return 'letime';
// }

// export const ArSpaceService = {
//   /**
//    * 取得 AR 空間清單（需要登入）
//    * - 預設使用 apiClient（自動附帶 Authorization）
//    * - 若後端開放匿名取得，傳 { withAuth:false } 會改用 publicClient
//    */
//   async list(params: ArSpaceListParams, opts?: { withAuth?: boolean }) {
//     const client = opts?.withAuth === false ? publicClient : apiClient;

//     // ✅ 確保 order_by 一定存在且預設 letime
//     const normalizedParams = {
//       ...params,
//       order_by: normalizeOrderBy((params as any)?.order_by),
//     } satisfies ArSpaceListParams;

//     const picked = pickDefined(normalizedParams);

//     // ✅ Debug：確認你實際送出的 order_by 是不是 letime
//     console.log('[ArSpace.list] request params:', picked);

//     const res = await client.get<ApiEnvelope<ArSpaceListData>>('/ar_space/mix', {
//       params: picked,
//     });

//     const raw = res.data;

//     // Debug：後端原始回傳
//     console.log('[ArSpace.list] raw response:', raw);

//     let data = raw.data;

//     if (Array.isArray(data)) {
//       data = {
//         items: data as ArSpace[],
//         page: Number(normalizedParams.page ?? 1),
//         page_size: (data as ArSpace[]).length,
//         total: (data as ArSpace[]).length,
//       } satisfies ArSpaceListData;
//       return data;
//     }

//     return ensureOk<ArSpaceListData>(raw);
//   },

//   async getArSpaceGroup(opts?: {
//     num?: number;
//     start?: number;
//     withAuth?: boolean;
//   }): Promise<ArSpaceGroup[]> {
//     const num = opts?.num ?? 50;
//     const start = opts?.start ?? 0;

//     const client = opts?.withAuth === false ? publicClient : apiClient;

//     const { data } = await client.get<ApiEnvelope<ArSpaceGroup[]>>('/ar_space/group', {
//       params: { num, start },
//     });

//     return ensureOk<ArSpaceGroup[]>(data);
//   },

//   async getMyArSpace() {
//     const { data } = await apiClient.get<ApiEnvelope<ArSpaceListResponse>>('/ar_space/list');
//     return ensureOk<ArSpaceListResponse>(data);
//   },

//   async getUnitArSpace(id: number | string, opts?: { withAuth?: boolean }) {
//     const client = opts?.withAuth === false ? publicClient : apiClient;
//     const { data } = await client.get<ApiEnvelope<ArSpaceDetail>>(`/ar_space/${id}`);
//     return ensureOk<ArSpaceDetail>(data);
//   },

//   async getIndexArSpace(opts?: { withAuth?: boolean }): Promise<ArSpaceIndexItem[]> {
//     const client = opts?.withAuth === false ? publicClient : apiClient;

//     type ArSpaceIndexDataLite = Omit<ArSpaceIndexData, 'article_data'>;

//     const { data } = await client.get<ApiEnvelope<ArSpaceIndexDataLite[]>>(
//       '/ar_space/index_ar_space'
//     );

//     const rawList = ensureOk<ArSpaceIndexDataLite[]>(data);

//     const normalizedList: ArSpaceIndexItem[] = rawList.map((item) => ({
//       ...(item as unknown as ArSpaceIndexData),
//       id: `${item.type}_${item.type_id}`,
//       article_data: [],
//     }));

//     return normalizedList;
//   },

//   async getIndexSpaceArticles(
//     params: { type: 'ar_space' | 'branch'; type_id: number | string },
//     opts?: { withAuth?: boolean }
//   ): Promise<NonNullable<ArSpaceIndexItem['article_data']>> {
//     if (params.type === 'branch') {
//       const detail = await ArSpaceService.getStoreArSpace(params.type_id, opts);
//       return detail.article_data ?? [];
//     }

//     const detail = await ArSpaceService.getUserArSpace(params.type_id, opts);
//     return detail.article_data ?? [];
//   },

//   async getUserArSpace(id: number | string, opts?: { withAuth?: boolean }) {
//     const client = opts?.withAuth === false ? publicClient : apiClient;

//     const { data } = await client.get<ApiEnvelope<ArSpaceUserDetail>>(`/ar_space/ar_space/${id}`);
//     return ensureOk<ArSpaceUserDetail>(data);
//   },

//   async getStoreArSpace(id: number | string, opts?: { withAuth?: boolean }) {
//     const client = opts?.withAuth === false ? publicClient : apiClient;

//     const { data } = await client.get<ApiEnvelope<ArSpaceStoreDetailRaw>>(`/ar_space/branch/${id}`);

//     const raw = ensureOk<ArSpaceStoreDetailRaw>(data);
//     const normalized = normalizeArSpaceStoreDetail(raw);
//     return normalized;
//   },

//   async addNewArSpace(payload: ArSpaceCreateFormPayload): Promise<ArSpaceCreateFormResult> {
//     const form = toFormData({
//       title: payload.title,
//       branch_name: payload.branch_name,
//       ar_photo: payload.ar_photo,
//       place_id: payload.place_id,
//       store_id: payload.store_id ?? 0,
//     });

//     const { data } = await apiClient.post<ApiEnvelope<ArSpaceCreateFormResult>>('/ar_space', form);

//     return ensureOk<ArSpaceCreateFormResult>(data);
//   },

//   async updateArSpace(payload: ArSpaceUpdateFormPayload): Promise<ArSpaceUpdateFormResult> {
//     const form = toFormData({
//       id: payload.id,
//       title: payload.title,
//       ar_photo: payload.ar_photo,
//     });

//     const { data } = await apiClient.post<ApiEnvelope<ArSpaceUpdateFormResult>>(
//       `/ar_space/update/${payload.id}`,
//       form
//     );

//     return ensureOk<ArSpaceUpdateFormResult>(data);
//   },

//   async deleteUnitArSpace(id: number | string) {
//     await apiClient.delete<ApiEnvelope>(`/ar_space/${id}`);
//     return;
//   },
// };
